import i18n from 'simple-react-i18n'
import Input from '../../../../components/forms/Input'
import { getDate, getDateWithHour, getHour } from '../../../../utils/DateUtil'
import { onChangeDate, onChangeHour } from '../../../../utils/FormUtils'
import React, { useState } from 'react'
import Button from '../../../../components/forms/Button'
import PluviometryAction from '../../../actions/PluviometryAction'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import ConfirmModal from '../../../../components/modal/ConfirmModal'
import NumberField from '../../../../components/forms/NumberField'
import ToastrAction from '../../../../toastr/actions/ToastrAction'
import { isNull } from 'lodash'
import { measurePluvioType } from '../../../shapes/PluvioShapes'
import { Card, CardContent, Grid, Stack, Typography } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'

const CorrectionPluvioToolPanel = ({
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    isChange,
    setIsChange,
    createTechnicalEvent,
    measuresSelectedByFilters,
    setSerieLinePrevious,
    isPreview,
    setIsPreview,
    valueCorrection,
    setValueCorrection,
    measuresData,
}) => {
    const [modalOpen, setModalOpen] = useState(false)

    const dispatch = useDispatch()

    const len = measuresSelectedByFilters?.length
    const diff = valueCorrection - measuresSelectedByFilters[len - 1]?.value
    const lastDate = measuresSelectedByFilters[len - 1]?.date
    const timeDiff = lastDate - measuresSelectedByFilters[0]?.date

    const onValidate = () => {
        if (isNull(startDate) || isNull(endDate)) {
            dispatch(ToastrAction.warning(i18n.datesNoSelected))
        }
        setIsPreview(true)
        const getDiff = (measure) => diff * (1 - ((lastDate - measure) / timeDiff))

        const dataPluvio = measuresData[0].measures.map(d => ([ d.date, d.value ]))

        const findIndexStartDate = dataPluvio.findIndex(m => m[0] === startDate) - 1
        const measureBeforeStartDate = dataPluvio[findIndexStartDate]

        const findIndexEndDate = dataPluvio.findIndex(m => m[0] === endDate) + 1
        const measureAfterStartDate = dataPluvio[findIndexEndDate]

        const measuresPrevious = measuresSelectedByFilters.map(m => ({
            date: m.date,
            value: m.value + getDiff(m.date),
        }),
        )

        const allMeasuresPreview = [
            ...(measureBeforeStartDate ? [{
                date: measureBeforeStartDate[0],
                value: measureBeforeStartDate[1],
            }] : []),
            ...measuresPrevious,
            ...(measureAfterStartDate ? [{
                date: measureAfterStartDate[0],
                value: measureAfterStartDate[1],
            }] : []),
        ]
        setSerieLinePrevious(allMeasuresPreview)
    }

    const onCancel = () => {
        setIsPreview(false)
        setSerieLinePrevious(measuresSelectedByFilters)
    }

    const onSave = () => {
        setIsPreview(false)
        setModalOpen(true)

        const getDiff = (measure) => diff * (1 - ((lastDate - measure) / timeDiff))

        const measuresChanges = measuresSelectedByFilters.map(m => ({
            ...m,
            value: m.value + getDiff(m.date),
            updated: true,
        }),
        )

        return (
            <ConfirmModal
                isOpen={modalOpen}
                title={i18n.driftingCorrection}
                onValidate={
                    dispatch(PluviometryAction.updateMeasures(measuresChanges))
                        .then(() => setIsChange(!isChange))
                        .then(() => createTechnicalEvent({ mode: 'correction' }))

                }
                onClose={() => setModalOpen(false)}
            />
        )
    }

    const resetDates = () => {
        setStartDate(null)
        setEndDate(null)
    }

    return (
        <Card>
            <CardTitle title={ i18n.driftingCorrection }/>
            <CardContent>
                <Stack spacing={1}>
                    <Grid container size={12} spacing={1} justifyContent='center'>
                        <Button
                            tooltip={ i18n.resetDates }
                            icon='loop'
                            className='margin-left-1 validToolBtn'
                            onClick={ () => resetDates() }
                            disabled={ isPreview }
                        />
                        <Typography paddingTop={1}>
                            { i18n.resetDates }
                        </Typography>
                    </Grid>
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <Input
                                title={ i18n.startDate }
                                id='startDate'
                                value={ getDate(startDate)}
                                onChange={ v => {
                                    onChangeDate(v, v2 => {
                                        return setStartDate(getDateWithHour(v2, startDate).valueOf())
                                    })
                                } }
                                disabled={ isPreview }
                            />
                        </Grid>
                        <Grid size={6}>
                            <Input
                                title={ i18n.startHour }
                                value={ getHour(startDate) }
                                onChange={ v => {
                                    onChangeHour(v, v2 => {
                                        return setStartDate(getDateWithHour(startDate, v2).valueOf())
                                    })
                                } }
                                disabled={ isPreview }
                            />
                        </Grid>
                        <Grid size={6}>
                            <Input
                                id='endDate'
                                value={ getDate(endDate) }
                                title={ i18n.endDate }
                                onChange={ v => {
                                    onChangeDate(v, v2 => {
                                        return setEndDate(getDateWithHour(v2, endDate).valueOf())
                                    })
                                } }
                                disabled={ isPreview }
                            />
                        </Grid>
                        <Grid size={6}>
                            <Input
                                id='endDateHour'
                                title={ i18n.endHour }
                                value={ getHour(endDate) }
                                onChange={ v => {
                                    onChangeHour(v, v2 => {
                                        return setEndDate(getDateWithHour(endDate, v2).valueOf())
                                    })
                                } }
                                disabled={ isPreview }
                            />
                        </Grid>
                    </Grid>
                    <NumberField
                        title={ i18n.value }
                        value={ valueCorrection }
                        onChange={ v => setValueCorrection(v) }
                        disabled={ isPreview }
                        floatValue
                    />
                    <Grid container size={12} justifyContent='center'>
                        <Button
                            tooltip={ i18n.apply }
                            onClick={ onValidate }
                            icon='border_color'
                            className='btn-floating btn-large'
                            disabled={ isPreview }
                        />
                        <Button
                            tooltip={ i18n.cancel }
                            onClick={ onCancel }
                            icon='cancel'
                            className='red btn-floating btn-large margin-left-2 margin-right-2'
                            disabled={ !isPreview }
                        />
                        <Button
                            tooltip={ i18n.register }
                            onClick={ onSave }
                            icon='save'
                            disabled={ !isPreview }
                            className={ `btn-floating btn-large ${isPreview ? 'pulse' : ''}` }
                        />
                    </Grid>
                </Stack>
            </CardContent>
        </Card>
    )
}


CorrectionPluvioToolPanel.propTypes = {
    startDate: PropTypes.number,
    setStartDate: PropTypes.func,
    endDate: PropTypes.number,
    setEndDate: PropTypes.func,
    isChange: PropTypes.bool,
    setIsChange: PropTypes.func,
    isPreview: PropTypes.bool,
    setIsPreview: PropTypes.func,
    createTechnicalEvent: PropTypes.func,
    measuresSelectedByFilters: PropTypes.arrayOf(measurePluvioType),
    setSerieLinePrevious: PropTypes.func,
    measuresData: PropTypes.arrayOf(measurePluvioType),
    valueCorrection: PropTypes.number,
    setValueCorrection: PropTypes.func,
}

export default CorrectionPluvioToolPanel
